/**
 * LAW 59 (18 October 2026) · THE CUT TICKETS. OWNER: web-frontend-engineer.
 *
 * The owner: "build the cut tickets into pauls backend so he could do this on
 * ipad … make it so paul can write using apple pen and sketch if needed. he
 * also sends 3 photos to tailor".
 *
 * Today Paul prints one sheet, cuts it into thirds and staples each third to
 * its cloth for the factory in Thailand. This page is that sheet, already cut:
 * ONE TICKET A GARMENT, each its own sheet of paper with a dashed box where the
 * staple goes, the body it is cut to, the decisions, the cloth and its metres,
 * and a pad he draws and writes on with the pencil. The client's photographs
 * print last, on a sheet of their own. The paper is the printer's: A4 in the
 * factory, Letter in Las Vegas, and the longest ticket fits either.
 *
 * ── WHAT THIS PAGE DOES NOT DO ────────────────────────────────────────────
 * It derives nothing. The tickets are projected on the server from the frozen
 * cut sheet (apps/server/src/domain/cut-tickets.ts) and read on their own
 * route, apart from the order; a value is the sheet's, worded the way the
 * record words it (`renderValue`), a sentence is the server's sentence, and a
 * measurement is never edited, only printed (domain law A). The one thing
 * written from here is Paul's drawing, as strokes, appended.
 *
 * ── EVERY WORD IS BILINGUAL THE HOUSE'S WAY ───────────────────────────────
 * `Word` (components/bilingual.tsx) prints the English, the catalogue's word
 * for the term and the same one the sheet prints, then the signed Thai under
 * `lang="th"`, or a small EN mark where nobody has signed it. The legend for
 * the mark prints at the foot while the server counts fewer words signed than
 * the tickets carry.
 *
 * `CutTicketsDocument` is the page without the network, so a test can render it
 * whole; `CutTicketsScreen` loads the tickets and owns the two calls (the save
 * of a sketch, and the record that the tickets were printed).
 */

import { Fragment, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { t } from '@pt/copy';
import { dateInWords, dateTimeInWords, integer, metres } from '@pt/view';
import { api } from '../../lib/api.ts';
import { patternLabel, present, weaveLabel } from '../../lib/label.ts';
import { useResource } from '../../lib/resource.ts';
import type {
  CutTicket,
  CutTicketsPayload,
  PhotographSet,
  SketchDrawing,
  TicketCloth,
  TicketSketch,
} from '../../lib/contracts.ts';
import { AppShell } from '../../app/AppShell.tsx';
import { useAnnouncer } from '../../app/Announcer.tsx';
import { useNavigation } from '../../app/Router.tsx';
import { useSession } from '../../app/SessionProvider.tsx';
import { Word, bilingualTermLabel } from '../../components/bilingual.tsx';
import { PhotographStrip } from '../../components/photographs.tsx';
import { Button, Statement } from '../../components/primitives.tsx';
import { renderValue } from '../../components/record.tsx';
import { SketchPad } from '../../components/sketchpad.tsx';
import { FetchError, LoadingRegion, OfflineStatement, Refused } from '../../components/states.tsx';

/**
 * Set on the document's root while the tickets are on the page, so the
 * stylesheet's print rules reach the shell (its column, its title) with plain
 * descendant selectors (components.css, LAW 59 on paper).
 */
const ROOT_CLASS = 'pt-page--tickets';

/* -------------------------------------------------------------- the values */

/**
 * A cell as the record words it (`renderValue`, in centimetres): a figure and
 * its unit, or the catalogue's word for an enum id. Blank where nothing was
 * taken, never "null".
 */
function cell(fieldId: string, value: number | string | null): string {
  if (value === null) return '';
  const { text, unit } = renderValue(fieldId, value, 'cm');
  return `${text} ${unit}`.trim();
}

/** A code the factory matches a bolt by (the mill's reference, the colour's), in the identifier voice. */
const code = (value: string): ReactNode => <span className="pt-ticket__code">{value}</span>;

/** The parts of a line, a middle dot between each, written once. */
function dotted(parts: readonly ReactNode[]): ReactNode[] {
  return parts.map((part, index) => (
    <Fragment key={index}>
      {index === 0 ? null : ' · '}
      {part}
    </Fragment>
  ));
}

/* ---------------------------------------------------------------- a ticket */

interface TicketProps {
  readonly payload: CutTicketsPayload;
  readonly ticket: CutTicket;
  readonly canDraw: boolean;
  readonly onSaveSketch: CutTicketsDocumentProps['onSaveSketch'];
}

/** Who and which: the same two facts head every printed page, so a page that comes loose can be put back. */
function Identity({ payload }: { payload: CutTicketsPayload }) {
  return (
    <dl className="pt-ticket__facts">
      <div className="pt-ticket__fact">
        <dt className="pt-ticket__label">
          <Word word={payload.words.client} />
        </dt>
        <dd className="pt-ticket__client">{payload.clientName}</dd>
      </div>
      <div className="pt-ticket__fact">
        <dt className="pt-ticket__label">
          <Word word={payload.words.order} />
        </dt>
        <dd className="pt-ticket__ref">{payload.orderRef}</dd>
      </div>
    </dl>
  );
}

function Measurements({ payload, ticket }: { payload: CutTicketsPayload; ticket: CutTicket }) {
  if (ticket.measurements.length === 0) return null;
  /* A column that would be blank on every row is not printed: the ease set
     does not reach every garment. A row's blank stays blank. */
  const finished = ticket.measurements.some((row) => row.finished !== null);
  return (
    <section className="pt-ticket__block pt-ticket__measures">
      <table className="pt-ticket__table">
        <thead>
          <tr>
            <th scope="col">
              <Word word={payload.words.measurements} />
            </th>
            <th scope="col" className="pt-ticket__num">
              <Word word={payload.words.body} />
            </th>
            {finished ? (
              <th scope="col" className="pt-ticket__num">
                <Word word={payload.words.finished} />
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {ticket.measurements.map((row) => (
            <tr key={row.id}>
              <th scope="row">
                <Word word={row.label} />
              </th>
              <td className="pt-ticket__num">{cell(row.id, row.value)}</td>
              {finished ? <td className="pt-ticket__num">{cell(row.id, row.finished)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
      {/* Paul's reading of the column. On paper the bilingual header says it,
          and the sheet needs the line for the jacket's twenty-nine rows. */}
      {finished ? <p className="pt-ticket__fine pt-noprint">{t('tickets.finished.note')}</p> : null}
    </section>
  );
}

function Make({ payload, ticket }: { payload: CutTicketsPayload; ticket: CutTicket }) {
  if (ticket.decisions.length === 0) return null;
  return (
    <section className="pt-ticket__block pt-ticket__make">
      <h3 className="pt-ticket__heading">
        <Word word={payload.words.make} />
      </h3>
      <dl className="pt-ticket__pairs">
        {ticket.decisions.map((row) => (
          <div className="pt-ticket__pair" key={row.optionId}>
            <dt>
              <Word word={row.label} />
            </dt>
            <dd>
              <Word word={row.value} />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Paul's making notes off the record, as he wrote them. Nothing at all where there are none. */
function Notes({ notes }: { notes: string | null }) {
  if (notes === null) return null;
  return (
    <section className="pt-ticket__block pt-ticket__remarks">
      <h3 className="pt-ticket__heading">{t('tickets.notes')}</h3>
      <p className="pt-ticket__notes">{notes}</p>
    </section>
  );
}

function Cloth({ payload, cloth }: { payload: CutTicketsPayload; cloth: TicketCloth }) {
  const hex = cloth.colourSrgbHex;
  /* Each line is the facts the sheet has, in order; a code is set apart. */
  const source = [...present([cloth.mill]), ...present([cloth.millRef]).map(code)];
  const kind = [
    ...present([cloth.colourName]),
    ...present([hex]).map(code),
    /* A weave and a pattern that are the same word ("Birdseye",
       "Herringbone") are one fact, said once. */
    ...new Set(
      present([
        weaveLabel(cloth.weave) ?? cloth.weave,
        patternLabel(cloth.pattern) ?? cloth.pattern,
        cloth.weightGsm === null ? null : t('tickets.cloth.weight', { weightGsm: integer(cloth.weightGsm) }),
      ]),
    ),
  ];
  return (
    <section className="pt-ticket__block pt-ticket__cloth">
      <h3 className="pt-ticket__heading">
        <Word word={payload.words.cloth} />
      </h3>
      {cloth.name === '' ? null : <p className="pt-ticket__clothName">{cloth.name}</p>}
      {source.length === 0 ? null : <p className="pt-ticket__line">{dotted(source)}</p>}
      {kind.length === 0 ? null : (
        <p className="pt-ticket__line">
          {/* Decorative: the code is printed beside it, and on paper the cloth
              itself is stapled to the ticket, so the square does not print. */}
          {hex === '' ? null : (
            <span className="pt-colourmark pt-ticket__swatch" style={{ backgroundColor: hex }} aria-hidden="true" />
          )}
          {dotted(kind)}
        </p>
      )}

      {/* This garment's own term of the house cloth table, with the server's
          sentence saying what it is. No figure at all where there is none. */}
      {cloth.metresForGarment === null ? null : (
        <div className="pt-ticket__metres">
          <dl className="pt-ticket__pair">
            <dt>
              <Word word={payload.words.metres} />
            </dt>
            <dd className="pt-ticket__metresFigure">
              {t('tickets.metres.value', { metreage: metres(cloth.metresForGarment) })}
            </dd>
          </dl>
          <p className="pt-ticket__fine">{cloth.metresStatement}</p>
        </div>
      )}
    </section>
  );
}

/**
 * The pad for one garment, and where its save stands. The drawing is held
 * here while Paul works, from what the server sent when the document mounted
 * (the document is keyed on the order); what the server kept on a save
 * arrives back as `saved` and, when nothing new has gone down meanwhile, as
 * the drawing itself.
 */
function TicketSketchPad({
  ticket,
  canDraw,
  onSaveSketch,
}: {
  ticket: CutTicket;
  canDraw: boolean;
  onSaveSketch: CutTicketsDocumentProps['onSaveSketch'];
}) {
  const [saved, setSaved] = useState<TicketSketch | null>(ticket.sketch);
  const [drawing, setDrawing] = useState<SketchDrawing | null>(ticket.sketch);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const label = t('tickets.sketch.canvas', { garment: bilingualTermLabel(ticket.garmentWord) });

  if (!canDraw) return <SketchPad drawing={ticket.sketch} label={label} saved={ticket.sketch} />;

  const save = () => {
    const sent = drawing;
    if (sent === null || saving) return;
    setSaving(true);
    setFailed(false);
    onSaveSketch(ticket.garment, { width: sent.width, height: sent.height, strokes: sent.strokes }).then(
      (result) => {
        setSaved(result.sketch);
        // What the server kept replaces what was sent, unless he has drawn on since.
        setDrawing((current) => (current === sent ? result.sketch : current));
        setSaving(false);
      },
      () => {
        setFailed(true);
        setSaving(false);
      },
    );
  };

  return (
    <SketchPad
      drawing={drawing}
      onChange={setDrawing}
      label={label}
      saved={saved}
      onSave={save}
      saving={saving}
      failed={failed}
    />
  );
}

function Ticket({ payload, ticket, canDraw, onSaveSketch }: TicketProps) {
  return (
    <article className="pt-ticket">
      <header className="pt-ticket__head">
        {/* Where the staple goes through the paper into the cloth: kept empty. */}
        <div className="pt-ticket__staple">
          <div className="pt-ticket__stapleBox" aria-hidden="true" />
          <p className="pt-ticket__stapleLabel">
            <Word word={payload.words.staple} />
          </p>
        </div>
        <div className="pt-ticket__who">
          <h2 className="pt-ticket__garment">
            <Word word={ticket.garmentWord} />
          </h2>
          <Identity payload={payload} />
        </div>
        <div className="pt-ticket__count">
          <p className="pt-ticket__page">{t('tickets.page', { index: ticket.index, count: ticket.count })}</p>
          <p className="pt-ticket__date">{dateInWords(payload.placedAt)}</p>
        </div>
      </header>

      <div className="pt-ticket__grid">
        <Measurements payload={payload} ticket={ticket} />
        <div className="pt-ticket__side">
          <Make payload={payload} ticket={ticket} />
          <Notes notes={ticket.notes} />
          <Cloth payload={payload} cloth={ticket.cloth} />
        </div>
      </div>

      <section className="pt-ticket__sketch">
        <h3 className="pt-ticket__heading">
          <Word word={payload.words.sketch} />
        </h3>
        {canDraw ? <p className="pt-ticket__fine pt-noprint">{t('tickets.sketch.lede')}</p> : null}
        <TicketSketchPad ticket={ticket} canDraw={canDraw} onSaveSketch={onSaveSketch} />
      </section>
    </article>
  );
}

/* ---------------------------------------------------------- the photographs */

/**
 * The three photographs the factory gets with the bundle, on a sheet of their
 * own after the last ticket: the client screen's strip, three across and as
 * large as three across allows, in the order the server gives them.
 */
function Photographs({ payload, set }: { payload: CutTicketsPayload; set: PhotographSet | null }) {
  return (
    <section className="pt-ticket-photos">
      <h2 className="pt-ticket__garment">
        <Word word={payload.words.photographs} />
      </h2>
      <Identity payload={payload} />
      {set === null ? (
        <Statement>{t('tickets.photographs.none')}</Statement>
      ) : (
        <>
          <PhotographStrip photographs={set.photographs} size="tickets" />
          <p className="pt-ticket__line">
            {t('tickets.photographs.taken', { when: dateInWords(set.takenAt), authorName: set.takenByName })}
          </p>
          {/* OR-2: the set the coat is cut to, or one kept for comparison. */}
          {set.governs ? null : <Statement>{t('client.photographs.superseded')}</Statement>}
          <Statement>
            <Word word={payload.words.photographsNote} />
          </Statement>
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ the document */

export interface CutTicketsDocumentProps {
  readonly payload: CutTicketsPayload;
  /** The governing photograph set the tickets print with, or null where none is on file. */
  readonly photographs: PhotographSet | null;
  /** The back office draws; the workshop reads (the save route is BACKOFFICE). */
  readonly canDraw: boolean;
  /** Save one garment's drawing; resolves with what the server kept. */
  readonly onSaveSketch: (garment: string, drawing: SketchDrawing) => Promise<{ readonly sketch: TicketSketch }>;
  readonly onPrint?: () => void;
  readonly onBack?: () => void;
}

/**
 * The tickets, the photographs page and the footer, with no network of its
 * own. On paper: one sheet a ticket, the photographs on the sheet after.
 */
export function CutTicketsDocument({
  payload,
  photographs,
  canDraw,
  onSaveSketch,
  onPrint,
  onBack,
}: CutTicketsDocumentProps) {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add(ROOT_CLASS);
    return () => root.classList.remove(ROOT_CLASS);
  }, []);

  return (
    <div className="pt-tickets">
      <Statement className="pt-noprint">{t('tickets.lede')}</Statement>
      {onPrint === undefined && onBack === undefined ? null : (
        <div className="pt-actions pt-noprint">
          {onPrint === undefined ? null : (
            <Button variant="primary" onClick={onPrint}>
              {t('tickets.action.print')}
            </Button>
          )}
          {onBack === undefined ? null : <Button onClick={onBack}>{t('tickets.action.back')}</Button>}
        </div>
      )}

      <div className="pt-tickets__list">
        {payload.tickets.map((ticket) => (
          <Ticket
            key={ticket.garment}
            payload={payload}
            ticket={ticket}
            canDraw={canDraw}
            onSaveSketch={onSaveSketch}
          />
        ))}
      </div>

      <Photographs payload={payload} set={photographs} />

      <div className="pt-tickets__foot">
        {/* The count, from the server that did the counting. */}
        <Statement>{payload.statement}</Statement>
        {payload.signedCount < payload.wordCount ? <Statement>{t('thai.mark.legend')}</Statement> : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- the screen */

export function CutTicketsScreen({ orderRef }: { orderRef: string }) {
  const { navigate } = useNavigation();
  const { session } = useSession();
  const { say, refuse } = useAnnouncer();
  const resource = useResource(() => api.cutTickets(orderRef), [orderRef]);
  const data = resource.data;
  const back = () => navigate(`/atelier/orders/${orderRef}`);
  const banner = resource.phase === 'offline' ? <OfflineStatement /> : null;

  if (data === null) {
    /* A refusal is the refusal, as on the order itself: the order is scoped to
       the people working on it, and "does it exist" is private. */
    const refused = resource.rejection?.body.code === 'NOT_FOUND_OR_NOT_YOURS';
    return (
      <AppShell title={t('tickets.heading')} banner={banner}>
        {resource.phase === 'skeleton' || resource.phase === 'idle' ? (
          <LoadingRegion rows={10} variant="ledger" slow={resource.slow} label={t('tickets.heading')} />
        ) : null}
        {resource.phase !== 'error' ? null : refused ? <Refused kind={404} /> : <FetchError onRetry={resource.reload} />}
      </AppShell>
    );
  }

  /* The save route is BACKOFFICE; a pad the workshop could draw on would only
     be refused at the door. */
  const canDraw = session?.role === 'master_tailor' || session?.role === 'admin';

  return (
    <AppShell title={t('tickets.heading')} banner={banner}>
      {data.tickets === null ? (
        <>
          <Statement>{t('tickets.absent')}</Statement>
          <div className="pt-actions">
            <Button onClick={back}>{t('tickets.action.back')}</Button>
          </div>
        </>
      ) : (
        <CutTicketsDocument
          key={data.orderId}
          payload={data.tickets}
          photographs={data.photographs}
          canDraw={canDraw}
          onSaveSketch={(garment, drawing) =>
            api.saveTicketSketch(data.orderId, garment, drawing).then(
              (result) => {
                say(
                  t('tickets.sketch.saved', {
                    authorName: result.sketch.drawnByName,
                    when: dateTimeInWords(result.sketch.drawnAt),
                  }),
                );
                return result;
              },
              (error: unknown) => {
                refuse(t('tickets.sketch.failed'));
                throw error;
              },
            )
          }
          onPrint={() => {
            void api.recordCutSheetPrint(data.orderId, { format: 'tickets' }).finally(() => window.print());
          }}
          onBack={back}
        />
      )}
    </AppShell>
  );
}
